import React, {
  ChangeEvent,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { IMessageRest, MessageAttachment } from "./types";
import { isMessageAttachmentValid, toMessageRequestFormData } from "./helpers";
import {
  Box,
  Button,
  FileUploadButton,
  Switch,
} from "@origin-digital/ods-core";
import { FileUploadButtonIcon, MarkAsReadButtonIcon } from "./Icons";
import { MessageContext } from "./MessageContext";
import { apiClient } from "../../../api/apiService";
import { AppContext } from "../../../context/AppContext";
import { MessageDirectionEnum } from "../../Premises/types";
import { useParams, useSearchParams } from "react-router-dom";
import { fromHyphenatedToCamelCase, normalizeFileName } from "../helper";
import FormContext from "../../../context/FormContext";
import { usePermissions } from "src/hooks/usePermissions";

interface IActionButtons {
  fetchMessages: () => Promise<void>;
}

const ActionButtons = ({ fetchMessages }: IActionButtons) => {
  const [sendingMessage, setSendingMessage] = useState(false);
  const [showMarkAsRead, setShowMarkAsRead] = useState(false);
  const [showPauseMessageSwitch, setShowPauseMessageSwitch] = useState(false);
  const [disablePauseMessageSwitch, setDisablePauseMessageSwitch] =
    useState(true);
  const { messageTabState, setMessageTabState } = useContext(MessageContext);
  const {
    requestId,
    journeyId,
    inputText,
    selectedAttachments,
    messageRecords,
    latestMessageDirection,
    latestMessageProcessed,
    latestMessageType,
    messagePause,
    type,
    blockRefId,
    processingMessagesFetched,
  } = messageTabState;
  const {
    context: { user },
  } = useContext(AppContext);
  const [searchParams] = useSearchParams();
  const defaultToMessageTab = searchParams.get("message");
  const { requestId: requestIdNavFrom } = useParams<{ requestId: string }>();
  const scrollToTargetRef = useRef<null | HTMLDivElement>(null);
  const {
    setBlocksExpanded,
    blocksExpanded,
    setMessageScrollingBlockRefId,
    messageScrollingBlockRefId,
  } = React.useContext(FormContext);
  const [scrollMessageTabToViewOnRender, setScrollMessageTabToViewOnRender] =
    useState(false);

  const { hasViewAllRequestsPermissions } = usePermissions();

  const isUnreadLatestMessageForLoginUser = (): boolean => {
    const isUnreadLatestMessageForCustomer =
      hasViewAllRequestsPermissions &&
      latestMessageProcessed === false &&
      latestMessageDirection === MessageDirectionEnum.OUTBOUND &&
      latestMessageType === type;
    const isUnreadLatestMessageForOriginUser =
      hasViewAllRequestsPermissions &&
      latestMessageProcessed === false &&
      latestMessageDirection === MessageDirectionEnum.INBOUND &&
      latestMessageType === type;
    return (
      isUnreadLatestMessageForCustomer || isUnreadLatestMessageForOriginUser
    );
  };

  useEffect(() => {
    if (defaultToMessageTab) {
      setScrollMessageTabToViewOnRender(true);
    }
  }, []);

  useEffect(() => {
    if (scrollToTargetRef) {
      setMessageTabState((prevMessageTabState) => ({
        ...prevMessageTabState,
        actionButtonRef: scrollToTargetRef,
      }));
    }
  }, [scrollToTargetRef]);

  useEffect(() => {
    const expandBlockAndScrollActionButtonIntoView = () => {
      if (requestId !== requestIdNavFrom) {
        return;
      }
      if (latestMessageType !== type) {
        return;
      }
      // messageScrollingBlockRefId as a lock to prevent racing condition among message tabs in difference blocks
      if (!messageScrollingBlockRefId) {
        setMessageScrollingBlockRefId(blockRefId);
        return;
      }
      if (
        scrollMessageTabToViewOnRender &&
        blockRefId &&
        isUnreadLatestMessageForLoginUser() &&
        messageScrollingBlockRefId === blockRefId
      ) {
        setBlocksExpanded({
          ...blocksExpanded,
          [fromHyphenatedToCamelCase(blockRefId)]: true,
        });
        setTimeout(() => {
          if (scrollToTargetRef.current && blockRefId) {
            scrollToTargetRef.current.scrollIntoView({
              behavior: "smooth",
              block: "end",
            });
          }
          onCustomerMarkAsRead();
          setScrollMessageTabToViewOnRender(false);
          setMessageScrollingBlockRefId(undefined);
        }, 600);
      }
    };
    expandBlockAndScrollActionButtonIntoView();
  }, [
    scrollMessageTabToViewOnRender,
    blockRefId,
    processingMessagesFetched,
    messageScrollingBlockRefId,
  ]);

  const onCustomerMarkAsRead = async () => {
    const latestMessageId =
      messageRecords.length > 0
        ? messageRecords[messageRecords.length - 1]?.id
        : null;
    const isLatestOutboundMessageUnProcessed =
      latestMessageId &&
      latestMessageDirection === MessageDirectionEnum.OUTBOUND &&
      latestMessageProcessed === false;
    if (
      defaultToMessageTab &&
      user?.role === "USER" &&
      isLatestOutboundMessageUnProcessed &&
      !messagePause
    ) {
      const markLatestOutboundMessageAsRead = async () => {
        await apiClient.post(`/message/markAsRead/${latestMessageId}`);
        setMessageTabState((prevMessageTabState) => ({
          ...prevMessageTabState,
          latestMessageProcessed: true,
        }));
      };
      await markLatestOutboundMessageAsRead();
    }
  };

  const handleError = (message: string) => {
    setMessageTabState((prevMessageTabState) => ({
      ...prevMessageTabState,
      displaySnackbar: {
        tone: "critical",
        message,
      },
    }));
  };

  const onOriginUserMarkAsRead = async () => {
    const latestMessageId = messageRecords[messageRecords.length - 1]?.id;
    if (
      latestMessageId &&
      latestMessageDirection === MessageDirectionEnum.INBOUND &&
      latestMessageProcessed === false
    ) {
      await apiClient.post(`/message/markAsRead/${latestMessageId}`);
      await fetchMessages();
    }
  };

  const onSave = async () => {
    if (journeyId) {
      if (!inputText || inputText.length === 0) {
        setMessageTabState((prevMessageTabState) => ({
          ...prevMessageTabState,
          showInputError: true,
        }));
        return;
      }
      try {
        setSendingMessage(true);
        const formData = toMessageRequestFormData(
          journeyId,
          inputText,
          type,
          selectedAttachments
        );
        await apiClient.post<IMessageRest>("/message", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        await fetchMessages();
        setMessageTabState((prevMessageTabState) => ({
          ...prevMessageTabState,
          inputText: "",
          selectedAttachments: [],
          displaySnackbar: {
            tone: "positive",
            message: "Message sent!",
          },
        }));
      } catch (error) {
        handleError("Failed to send message.");
      } finally {
        setSendingMessage(false);
      }
    }
  };

  const onAttach = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!isMessageAttachmentValid({ file, handleError })) {
      e.target.value = "";
      return;
    }
    setMessageTabState((prevMessageTabState) => ({
      ...prevMessageTabState,
      showDocumentTypeDialog: true,
      selectedAttachments: [
        ...selectedAttachments,
        {
          file,
          fileName: normalizeFileName(file!.name),
        } as MessageAttachment,
      ],
    }));
    e.target.value = "";
  };

  const onPauseMessage = async () => {
    await apiClient.post(`/message/messagePause/${journeyId}`, null, {
      params: { messagePause: !messagePause },
    });
    setMessageTabState((prevMessageTabState) => ({
      ...prevMessageTabState,
      messagePause: !messagePause,
    }));
  };

  useEffect(() => {
    if (user?.role !== "ORIGIN_ADMIN" && user?.role !== "ORIGIN_USER") {
      setShowMarkAsRead(false);
      setShowPauseMessageSwitch(false);
      return;
    }
    setShowPauseMessageSwitch(true);
    setDisablePauseMessageSwitch(
      !(
        messageRecords?.length > 0 &&
        latestMessageDirection === MessageDirectionEnum.OUTBOUND
      )
    );
    const isLatestInboundMessageUnProcessed =
      messageRecords.length > 0 &&
      latestMessageDirection === MessageDirectionEnum.INBOUND &&
      latestMessageProcessed === false;
    setShowMarkAsRead(isLatestInboundMessageUnProcessed);
  }, [
    user?.role,
    messageRecords,
    latestMessageDirection,
    latestMessageProcessed,
  ]);

  return (
    <Box
      id={`${blockRefId}-message`}
      display="flex"
      justifyContent={{
        xs: "flex-start",
        sm: "space-between",
      }}
      alignItems={{
        xs: "flex-start",
        sm: "center",
      }}
      flexDirection={{
        xs: "column",
        sm: "row",
      }}
      ref={scrollToTargetRef}
      //width="full"
    >
      {/* Left actions */}
      <Box
        display="flex"
        justifyContent={{
          xs: "flex-start",
          sm: "flex-start",
        }}
        alignItems="center"
        flexWrap="wrap"
        marginBottom={{
          xs: "large",
          sm: "none",
        }}
      >
        <Box
          marginRight={{
            xs: "small",
            sm: "xxsmall",
            md: "medium",
          }}
          marginBottom={{
            xs: "xsmall",
            sm: "none",
            md: "none",
          }}
        >
          <FileUploadButton
            variant="text"
            color="secondaryB"
            size="small"
            onFileUpload={(e) => onAttach(e)}
            icon={<FileUploadButtonIcon />}
          >
            Attach
          </FileUploadButton>
        </Box>
        <Box
          marginRight={{
            xs: "small",
            sm: "xsmall",
            md: "medium",
          }}
          marginBottom={{
            xs: "xsmall",
            sm: "none",
            md: "none",
          }}
        >
          {showMarkAsRead && (
            <Button
              variant="text"
              color="secondaryB"
              size="small"
              icon={<MarkAsReadButtonIcon />}
              onClick={onOriginUserMarkAsRead}
            >
              Mark as read
            </Button>
          )}
        </Box>
        {showPauseMessageSwitch && (
          <Switch
            disabled={disablePauseMessageSwitch}
            label="Pause (Require response)"
            checked={messagePause}
            onChange={onPauseMessage}
          />
        )}
      </Box>
      {/* End Left actions */}
      {/* Right actions */}
      <Button
        size="small"
        spinner={sendingMessage}
        fullWidth={{
          xs: true,
          sm: false,
        }}
        onClick={onSave}
      >
        Send
      </Button>
      {/* End Right actions */}
    </Box>
  );
};

export default ActionButtons;
